import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail, Trash2, RefreshCw, ChevronLeft, ChevronRight,
  Eye, X, Clock, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function AdminDevEmails() {
  const { t } = useTranslation();
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [mode, setMode] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewIdx, setPreviewIdx] = useState(null);

  const fetchEmails = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dev/emails`, { params: { page: p }, withCredentials: true });
      setEmails(res.data.emails || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setPages(res.data.pages || 1);
      setMode(res.data.mode || "live");
    } catch {
      toast.error("Gagal memuat email");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmails(1); }, [fetchEmails]);

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/admin/dev/emails`, { withCredentials: true });
      toast.success("Semua email dev dihapus");
      fetchEmails(1);
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  // Extract OTP from subject
  const extractOtp = (subject, html) => {
    if (!subject?.toLowerCase().includes("otp") && !subject?.toLowerCase().includes("verifikasi")) return null;
    const match = html?.match(/letter-spacing[^>]*>(\d{6})</);
    return match ? match[1] : null;
  };

  if (mode === "live") {
    return (
      <div className="p-6 md:p-8" data-testid="dev-emails-page">
        <div className="text-center py-20">
          <Mail className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="font-heading text-lg font-semibold mb-2">Email Log Mode Tidak Aktif</h2>
          <p className="text-sm text-muted-foreground">Set <code className="bg-secondary px-2 py-0.5 rounded text-xs">EMAIL_MODE=log</code> di backend .env untuk mengaktifkan.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="dev-emails-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold">Dev Email Viewer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Email tidak dikirim — hanya tersimpan di sini untuk testing.
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-verdict-real/10 text-verdict-real">LOG MODE</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-8" onClick={() => fetchEmails(page)} disabled={loading} data-testid="dev-email-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-8 text-verdict-fake hover:text-verdict-fake" onClick={clearAll} data-testid="dev-email-clear">
            <Trash2 className="w-3 h-3" /> Hapus Semua
          </Button>
        </div>
      </div>

      <div className="text-xs font-mono text-muted-foreground mb-4">{total} email tersimpan</div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-md border border-border bg-card animate-pulse" />)}</div>
      ) : emails.length === 0 ? (
        <div className="text-center py-16">
          <Mail className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Belum ada email. Trigger OTP atau aksi lain untuk melihat email di sini.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map((em, i) => {
            const otp = extractOtp(em.subject, em.html);
            const globalIdx = (page - 1) * 20 + i;
            return (
              <div key={i} className="rounded-md border border-border bg-card p-4 hover:bg-secondary/20 transition-colors" data-testid={`dev-email-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-heading font-semibold text-sm truncate">{em.subject}</span>
                      {otp && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded bg-brand-primary/15 text-brand-primary text-xs font-mono font-bold tracking-widest" data-testid={`otp-code-${i}`}>
                          OTP: {otp}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{em.to} ({em.to_name})</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(em.created_at)}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0" onClick={() => setPreviewIdx(previewIdx === globalIdx ? null : globalIdx)} data-testid={`preview-btn-${i}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
                {previewIdx === globalIdx && (
                  <div className="mt-3 border border-border rounded-md overflow-hidden" data-testid={`email-preview-${i}`}>
                    <div className="flex items-center justify-between bg-secondary/40 px-3 py-1.5">
                      <span className="text-[10px] font-mono text-muted-foreground">Preview Email</span>
                      <button onClick={() => setPreviewIdx(null)} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
                    </div>
                    <iframe
                      srcDoc={em.html}
                      title="Email preview"
                      className="w-full bg-white"
                      style={{ height: 400, border: "none" }}
                      sandbox=""
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <span className="text-xs font-mono text-muted-foreground">Halaman {page} dari {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0" onClick={() => fetchEmails(page - 1)} disabled={page <= 1}><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 w-7 p-0" onClick={() => fetchEmails(page + 1)} disabled={page >= pages}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
